import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"

import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import { Input } from "@components/ui/input"
import { Button } from "@components/ui/button"
import { Loader2 } from "lucide-react"

import {
  useCreateProspectMutation,
  useGetProspectQuery,
  useUpdateProspectMutation,
} from "@apis/prospects/useProspects"
import type { IProspectRequest } from "@apis/prospects/types"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select"
import { AddressInfoForm } from "@components/Address/AddressInfoForm"
import { useQueryClient } from "@tanstack/react-query"
import { prospectsKeys } from "@apis/prospects/queryKeys"
import { toast } from "sonner"
import { useDrawer } from "@contexts/DrawerContext"
import type { IProspect } from "@commonTypes/prospect"
import { useNavigate } from "react-router-dom"

type TProspectFormProps = {
  prospect_guid?: string
  onSuccess?: (prospect: IProspect) => void
}

export const ProspectForm = ({
  prospect_guid,
  onSuccess,
}: TProspectFormProps) => {
  const queryClient = useQueryClient()
  const { open, closeDrawer } = useDrawer()
  const navigate = useNavigate()
  const [showAddressInfoForm, setShowAddressInfoForm] = useState(false)
  const [targetProspectGuid, setTargetProspectGuid] = useState<
    string | undefined
  >(prospect_guid)
  const isEditOperation = Boolean(prospect_guid)
  const getProspectQuery = useGetProspectQuery(targetProspectGuid)
  const updateProspectMutation = useUpdateProspectMutation()
  const createProspectMutation = useCreateProspectMutation()

  const EMPTY_PROSPECT_FORM = {
    first_name: "",
    last_name: "",
    email: {
      email_address: "",
    },
    primary_phone: "",
    ...(isEditOperation && {
      extole_person_id: "",
      extole_share_url: "",
    }),
  }

  const serializeProspectFormData = useMemo(() => {
    if (!targetProspectGuid || !getProspectQuery.data) return undefined

    return {
      first_name: getProspectQuery.data.first_name || "",
      last_name: getProspectQuery.data.last_name || "",
      email: {
        email_address: getProspectQuery.data.email.email_address || "",
      },
      primary_phone: getProspectQuery.data.primary_phone || "",
      ...(isEditOperation && {
        extole_person_id: getProspectQuery.data.extole_person_id || "",
        extole_share_url: getProspectQuery.data.extole_share_url || "",
      }),
    }
  }, [targetProspectGuid, getProspectQuery.data, isEditOperation])

  const form = useForm<IProspectRequest>({
    mode: "onTouched",
    defaultValues: EMPTY_PROSPECT_FORM,
    values: serializeProspectFormData,
  })
  const { isDirty, isValid } = form.formState

  const handleSubmit = (formData: IProspectRequest) => {
    if (!targetProspectGuid) {
      createProspectMutation.mutate(
        { request: formData },
        {
          onSuccess: (prospect) => handleMutationSuccess(prospect),
          onError: () => handleMutationError(),
        },
      )
    } else {
      updateProspectMutation.mutate(
        {
          prospect_guid: targetProspectGuid,
          request: formData,
        },
        {
          onSuccess: (prospect) => handleMutationSuccess(prospect),
          onError: () => handleMutationError(),
        },
      )
    }
  }

  const handleMutationSuccess = (prospect: IProspect) => {
    const message = `Prospect successfully ${isEditOperation ? "updated" : "created"}!`

    toast.success(message)
    queryClient.invalidateQueries({
      queryKey: prospectsKeys.detail(prospect_guid),
    })
    closeDrawer()
    onSuccess?.(prospect)
  }

  const handleMutationError = () => {
    const message = `Error ${isEditOperation ? "updating" : "creating"} prospect!`
    toast.error(message)
  }

  // We need to create a prospect first before adding an creating an address
  // This was due to the fact that the create address endpoint needs the prospect_guid to link the address to the prospect
  // And in the create operation we don't have a prospect_guid yet, instead we do have it in the edit operation
  // This is the same workflow we had in OLD CRM, definitely not ideal but we need to keep it for now
  const handleAddAddress = async () => {
    try {
      if (!targetProspectGuid) {
        const response = await createProspectMutation.mutateAsync({
          request: EMPTY_PROSPECT_FORM,
        })
        const newProspectGuid = response?.prospect_guid
        setTargetProspectGuid(newProspectGuid)
      }

      setShowAddressInfoForm(true)
    } catch (error) {
      toast.error(`Error creating prospect for address! ${error}`)
    }
  }

  return (
    <>
      {showAddressInfoForm && targetProspectGuid ? (
        <AddressInfoForm
          identifier={targetProspectGuid}
          address_type_code="prospect"
          onSave={() => {
            queryClient.invalidateQueries({
              queryKey: prospectsKeys.detail(targetProspectGuid),
            })
            setShowAddressInfoForm(false)
          }}
          onCancel={() => setShowAddressInfoForm(false)}
          showMap
        />
      ) : (
        <Form {...form}>
          <div className="tw:flex tw:flex-col tw:gap-4">
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="tw:grid tw:grid-cols-12 tw:items-baseline tw:gap-4"
            >
              <div className="tw:bordered-container tw:col-span-12 tw:grid tw:grid-cols-subgrid tw:gap-4">
                <div className="tw:col-span-12 tw:md:col-span-6">
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <Select
                      value={
                        getProspectQuery.data?.addresses?.[0]?.address_guid
                      }
                    >
                      <FormControl className="tw:w-full">
                        <SelectTrigger>
                          <SelectValue placeholder="Select Prospect Address" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {getProspectQuery.data?.addresses?.map((option) => (
                          <SelectItem
                            key={option.address_guid}
                            value={option.address_guid}
                          >
                            {[
                              option.street,
                              option.city,
                              option.state_code,
                              option.zip,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                </div>

                <div className="tw:col-span-12 tw:flex tw:justify-end tw:self-end tw:md:col-span-6 tw:md:justify-start">
                  <Button
                    variant="outline"
                    type="button"
                    onClick={handleAddAddress}
                  >
                    {createProspectMutation.isPending ? (
                      <>
                        <Loader2 className="tw:animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <span>Add New Prospect Address</span>
                    )}
                  </Button>
                </div>
              </div>

              <div className="tw:col-span-12 tw:md:col-span-6">
                <FormField
                  control={form.control}
                  name="first_name"
                  rules={{ required: "First name is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter first name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="tw:col-span-12 tw:md:col-span-6">
                <FormField
                  control={form.control}
                  name="last_name"
                  rules={{ required: "Last name is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="tw:col-span-12 tw:md:col-span-6">
                <FormField
                  control={form.control}
                  name="email.email_address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Enter email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="tw:col-span-12 tw:md:col-span-6">
                <FormField
                  control={form.control}
                  name="primary_phone"
                  rules={{
                    required: "Primary phone is required",
                    pattern: {
                      value: /^\d{10}$/,
                      message: "Phone must be a 10-digit number",
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone number</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter phone number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {isEditOperation && (
                <div className="tw:col-span-12 tw:grid tw:grid-cols-subgrid tw:gap-4">
                  <div className="tw:col-span-12 tw:md:col-span-6">
                    <FormField
                      control={form.control}
                      name="extole_person_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Extole User ID</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter extole user ID"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="tw:col-span-12 tw:md:col-span-6">
                    <FormField
                      control={form.control}
                      name="extole_share_url"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Extole Share URL</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter extole share URL"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}

              <div className="tw:col-span-12 tw:flex tw:justify-end tw:gap-4">
                <Button
                  type="submit"
                  disabled={
                    !isDirty ||
                    !isValid ||
                    createProspectMutation.isPending ||
                    updateProspectMutation.isPending
                  }
                >
                  {(createProspectMutation.isPending ||
                    updateProspectMutation.isPending) &&
                  isValid ? (
                    <>
                      <Loader2 className="tw:animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>Save</span>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    return open ? closeDrawer() : navigate("/")
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </Form>
      )}
    </>
  )
}
